import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { MapPinIcon } from '@/components/icons';
import { getBestImageUrl } from '@/lib/utils';
import type { Business } from '@/features/booking/types/tableBookingTypes';

interface CartHeroSectionProps {
  business: Business | null;
}

const CartHeroSection: React.FC<CartHeroSectionProps> = ({ business }) => {
  const navigate = useNavigate();

  if (!business) return null;

  const backgroundImage = getBestImageUrl(business.card_image_id, business.media, '/placeholder-club.jpg');
  const location = business.location;

  return (
    <div className="relative min-h-[24rem] overflow-hidden pt-32">
      {/* Background Image - using CSS background-image for better performance */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
        }}
      />
      
      {/* Fallback gradient background - shown behind if image doesn't load */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900"
        style={{ zIndex: -1 }}
      />
      
      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
      
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 h-full flex items-end pb-12">
        <div className="space-y-4 w-full">
          {/* Business Name */}
          <h1 className="text-5xl lg:text-7xl font-serif font-bold text-white tracking-tight">
            {business.name}
          </h1>
          
          {/* Location */}
          {location && (
            <div className="flex items-center space-x-3 text-blue-400 font-medium">
              <MapPinIcon />
              <span>
                {location.address_line1 && `${location.address_line1}, `}
                {location.city}, {location.country}
              </span>
            </div>
          )}
          
          {/* Description */}
          {business.description && (
            <p className="text-lg text-gray-300 max-w-3xl">
              {business.description}
            </p>
          )}
          
          {/* Link to Venue Page */}
          <button
            onClick={() => navigate(`/venues/${business.handle}`)}
            className="inline-flex items-center px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg border border-white/30 transition-colors font-medium mt-4"
          >
            <Eye className="w-4 h-4 mr-2" />
            View Venue Page
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartHeroSection;


